import React, { useState, useRef } from 'react';
import { downloadSampleExcel } from '../sampleData';

const ExcelDropZone = ({ onFileUpload, isLoading, error, hasExistingData }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Check file type
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      alert('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
      return;
    }
    
    onFileUpload(file);
  };

  return (
    <div className="px-4 py-5 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {hasExistingData 
              ? 'Ajouter plus de contacts depuis Excel' 
              : 'Importer des contacts depuis Excel'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Glissez-déposez un fichier Excel ou cliquez pour parcourir
          </p>
        </div>

        {hasExistingData && (
          <div className="mb-6">
            <div className="rounded-md bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Vous ajoutez plus de contacts à votre liste. Les nouveaux contacts seront fusionnés avec vos contacts existants.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div 
          className={`relative border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors duration-200 ${
            isDragging 
              ? 'border-[#174540] bg-[#174540]/5' 
              : error 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300 hover:border-[#174540] hover:bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".xlsx,.xls"
            onChange={handleFileInput}
            disabled={isLoading}
          />
          
          {isLoading ? (
            <div className="animate-pulse">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg className="animate-spin h-10 w-10 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900">Traitement en cours...</p>
            </div>
          ) : error ? (
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="mt-2 text-sm font-medium text-red-800">{error}</p>
              <p className="mt-1 text-sm text-red-700">Cliquez pour essayer à nouveau</p>
            </div>
          ) : (
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-lg bg-[#174540]/10">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#174540]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-900">
                  {isDragging ? 'Déposez le fichier ici' : 'Sélectionnez un fichier Excel'}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Formats supportés: XLSX, XLS
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  Le fichier Excel doit contenir les colonnes suivantes: <span className="font-semibold">Prénom, Nom, Téléphone</span>
                </p>
                <p className="mt-3 text-sm md:mt-0 md:ml-6">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadSampleExcel();
                    }}
                    className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                  >
                    Télécharger un exemple
                    <span aria-hidden="true"> &rarr;</span>
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelDropZone;